



import { CLST, CL_UI } from '../lib/icons';
import { fmtDate } from '../lib/format';
import { toCsv, type CsvExport } from './csvExport';
import {
  formatAgentBuild,
  nodeUpdateLabel,
  nodeUpdateState,
  type ControlBuild,
} from '../domain/nodeBuild';
import type { Cluster, ClusterEngine, ClusterLive } from '../domain/panel';
export type { Cluster, ClusterEngine, ClusterLive, WarmupJob } from '../domain/panel';
export type { ControlBuild } from '../domain/nodeBuild';
export { formatAgentBuild, nodeUpdateLabel, nodeUpdateState };

const EMPTY_LIVE: ClusterLive = {
  cpuPct: 0,
  ramUsedGb: 0,
  profilesActive: 0,
  profilesIdle: 0,
  profilesTotal: 0,
  profilesDiskMb: 0,
  profilesDiskTotalMb: 0,
  proxiesTotal: 0,
  proxiesUsed: 0,
  profilesWarming: 0,
  profilesReady: 0,
  profilesReadyForTarget: 0,
  profilesDueToday: 0,
  loadPct: 0,
};


export function clLiveStats(c: Cluster): ClusterLive {
  return { ...EMPTY_LIVE, ...(c.live ?? {}) };
}


export function clUiStatus(c: Cluster): string {
  return CL_UI[c.status] || 'off';
}


export function clPillInfo(c: Cluster): { cls: string; title: string } {
  const key =
    c.status === 'busy' && c.live?.workMode === 'warming'
      ? 'warming'
      : c.status === 'busy' && c.live?.workMode === 'targeting'
        ? 'targeting'
        : c.status;
  const ui = CLST[key] ?? CLST.offline;
  const title = c.status === 'deploying' && c.deployStageLabel ? c.deployStageLabel : ui[1];
  return { cls: ui[0], title };
}

export const LIVE_STATUSES = ['online', 'busy', 'warming', 'targeting'];

export const isLiveStatus = (c: Cluster): boolean => LIVE_STATUSES.includes(c.status);

export function clusterOccupancy(c: Cluster): {
  running: number;
  cap: number;
  farm: number;
  target: number;
  queued: number;
  available: number;
} {
  const live = c.live ?? {};
  if (c.enabled === false || !LIVE_STATUSES.includes(c.status)) {
    return { running: 0, cap: 0, farm: 0, target: 0, queued: 0, available: 0 };
  }
  const farm = Math.max(0, live.farmRunning ?? 0);
  const target = Math.max(0, live.targetRunning ?? 0);
  const running = farm + target || Math.max(0, live.workersActive ?? live.profilesActive ?? 0);
  const cap = Math.max(running, live.workersMax ?? 0);
  const queued = Math.max(0, live.workersQueued ?? 0);
  return { running, cap, farm, target, queued, available: Math.max(0, cap - running) };
}

export function clusterEngines(c: Cluster | null | undefined): ClusterEngine[] {
  const raw = Array.isArray(c?.searchEngines) ? c.searchEngines : null;
  if (!raw || !raw.length) return ['bing', 'google'];
  const out: ClusterEngine[] = [];
  for (const e of raw) {
    const v = String(e || '').toLowerCase();
    if (v === 'google' && !out.includes('google')) out.push('google');
    else if (v === 'bing' && !out.includes('bing')) out.push('bing');
  }
  return out.length ? out : ['bing', 'google'];
}

export const engineLabel = (e: ClusterEngine): string => (e === 'google' ? 'Google' : 'Bing');

export function clusterOsLabel(c: { os?: string } | null | undefined): string {
  return c?.os === 'windows' ? 'Windows' : 'Linux';
}

export function matchesClusterFilter(
  c: Cluster,
  query: string,
  statusFilter: string,
  control?: ControlBuild | null,
): boolean {
  if (statusFilter === 'stale') {
    if (nodeUpdateState(control, c.agentRevision) !== 'stale') return false;
  } else {
    const uiSt = clUiStatus(c);
    if (statusFilter !== 'all' && uiSt !== statusFilter) return false;
  }
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${c.name} ${c.sshHost} ${c.region || ''} ${c.hostname || ''} ${c.agentRevision || ''}`.toLowerCase().includes(q);
}

export function nodesNeedingAgentUpdate(clusters: Cluster[], control?: ControlBuild | null): Cluster[] {
  const enabled = clusters.filter((c) => c.enabled && c.status !== 'deploying');
  const stale = enabled.filter((c) => nodeUpdateState(control, c.agentRevision) === 'stale');
  if (stale.length) return stale;
  return enabled.filter((c) => nodeUpdateState(control, c.agentRevision) === 'unknown');
}


export function applyClusterLivePatch(clusters: Cluster[], patches: Array<Partial<Cluster> & { id?: string }>): Cluster[] | null {
  let changed = false;
  const next = clusters.slice();
  for (const patch of patches) {
    if (!patch?.id) continue;
    const idx = next.findIndex((c) => c.id === patch.id);
    if (idx < 0) continue;
    const prev = next[idx];
    next[idx] = {
      ...prev,
      ...patch,
      searchEngines: patch.searchEngines ?? prev.searchEngines,
    } as Cluster;
    changed = true;
  }
  return changed ? next : null;
}

export function clustersCsv(clusters: Cluster[], control?: ControlBuild | null): CsvExport {
  return {
    name: 'clusters.csv',
    csv: toCsv(
      ['Имя', 'Хост', 'Регион', 'Статус', 'CPU %', 'ОЗУ ГБ', 'Профили', 'Агент', 'Ревизия', 'Обновление'],
      clusters.map((c) => {
        const live = c.live ?? {};
        return [
          c.name,
          `${c.sshHost}:${c.sshPort}`,
          c.region || '—',
          CLST[c.status]?.[1] || c.status,
          live.cpuPct ?? '—',
          live.ramUsedGb ?? '—',
          live.profilesTotal ?? '—',
          formatAgentBuild(c.agentVersion, c.agentRevision),
          c.agentRevision || '—',
          nodeUpdateLabel(nodeUpdateState(control, c.agentRevision)),
        ];
      }),
    ),
  };
}


export const shortDateTime = (iso: string | null | undefined): string => fmtDate(iso).slice(5);



let pendingProfileCluster: string | null = null;

export function setPendingProfileCluster(id: string | null): void {
  pendingProfileCluster = id;
}


export function takePendingProfileCluster(): string | null {
  const id = pendingProfileCluster;
  pendingProfileCluster = null;
  return id;
}

let pendingServerFocus: string | null = null;

export function setPendingServerFocus(id: string | null): void {
  pendingServerFocus = id;
}

export function takePendingServerFocus(): string | null {
  const id = pendingServerFocus;
  pendingServerFocus = null;
  return id;
}

let pendingLogCluster: string | null = null;

export function setPendingLogCluster(id: string | null): void {
  pendingLogCluster = id;
}

export function takePendingLogCluster(): string | null {
  const id = pendingLogCluster;
  pendingLogCluster = null;
  return id;
}
